import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2, VolumeX, X, Music } from 'lucide-react';

export default function MusicPlayer() {
  const [showMusicPlayer, setShowMusicPlayer] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    audioRef.current = new Audio('/music/rumble.mp3');
    audioRef.current.loop = true;
    audioRef.current.volume = 0.5; // Set initial volume to 50%

    // Update progress
    const audio = audioRef.current;
    const updateProgress = () => {
      if (audio.duration) {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };

    audio.addEventListener('timeupdate', updateProgress);
    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
      audio.pause();
    };
  }, []);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(error => {
        console.error('Error playing audio:', error);
      });
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <>
      {/* Music Player Toggle Button */}
      <button
        onClick={() => setShowMusicPlayer(!showMusicPlayer)}
        className="fixed bottom-6 right-6 z-50 p-4 rounded-full bg-primary-500/20 border border-primary-500/30 
          hover:bg-primary-500/30 transition-all duration-300 text-white backdrop-blur-sm
          shadow-lg shadow-primary-500/20 animate-pulse-slow"
      >
        <Music className="w-6 h-6" />
      </button>

      {/* Floating Music Player */}
      <AnimatePresence>
        {showMusicPlayer && (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", damping: 20 }}
            className="fixed bottom-24 right-6 z-40 w-80 p-4 rounded-2xl
              bg-gaming-dark/90 backdrop-blur-md border border-primary-500/30
              shadow-xl shadow-primary-500/20"
          >
            <div className="space-y-4">
              {/* Track Info */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center">
                  <Music className="w-6 h-6 text-primary-400" />
                </div>
                <div>
                  <h3 className="text-white font-bold">Rumble Theme</h3>
                  <p className="text-gray-400 text-sm">Game Soundtrack</p>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-between">
                <button
                  onClick={togglePlay}
                  className="p-2 rounded-full hover:bg-primary-500/20 transition-colors"
                >
                  {isPlaying ? (
                    <VolumeX className="w-6 h-6 text-white" />
                  ) : (
                    <Volume2 className="w-6 h-6 text-white" />
                  )}
                </button>
                <button
                  onClick={() => setShowMusicPlayer(false)}
                  className="p-2 rounded-full hover:bg-primary-500/20 transition-colors"
                >
                  <X className="w-6 h-6 text-white" />
                </button>
              </div>

              {/* Progress Bar */}
              <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-primary-500 to-amber-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
} 